import { Axis, Color4, Vector2, Vector3, Vector4 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Nullable } from '@babylonjs/core/types';

/**
 * Class containing static functions to help procedurally build meshes
 */
export class CylinderBuilder {
    /**
     * Creates a torus mesh
     * * The parameter `diameter` sets the diameter size (float) of the torus (default 1)
     * * The parameter `thickness` sets the diameter size of the tube of the torus (float, default 0.5)
     * * The parameter `tessellation` sets the number of torus sides (postive integer, default 16)
     * * You can also set the mesh side orientation with the values : BABYLON.Mesh.FRONTSIDE (default), BABYLON.Mesh.BACKSIDE or BABYLON.Mesh.DOUBLESIDE
     * * If you create a double-sided mesh, you can choose what parts of the texture image to crop and stick respectively on the front and the back sides with the parameters `frontUVs` and `backUVs` (Vector4). Detail here : https://doc.babylonjs.com/babylon101/Cylinderover_basic_elements#side-orientation
     * * The mesh can be set to updatable with the boolean parameter `updatable` (default false) if its internal geometry is supposed to change once created.
     * @param name defines the name of the mesh
     * @param options defines the options used to create the mesh
     * @param scene defines the hosting scene
     * @returns the torus mesh
     * @see https://doc.babylonjs.com/how_to/set_shapes#torus
     */
    public static CreateCylinder(
        name: string,
        options: {
            height?: number;
            diameterTop?: number;
            diameterBottom?: number;
            diameter?: number;
            tessellation?: number;
            subdivisions?: number;
            arc?: number;
            faceColors?: Color4[];
            faceUV?: Vector4[];
            hasRings?: boolean;
            enclose?: boolean;
            cap?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
            updatable?: boolean;
        },
        scene: any
    ): Mesh {
        const Cylinder = new Mesh(name, scene);

        options.sideOrientation = Mesh._GetDefaultSideOrientation(options.sideOrientation);
        Cylinder._originalBuilderSideOrientation = options.sideOrientation;

        const vertexData = createCylinder(options);

        vertexData.applyToMesh(Cylinder, options.updatable);

        return Cylinder;

        function createCylinder(options: {
            height?: number;
            diameterTop?: number;
            diameterBottom?: number;
            diameter?: number;
            tessellation?: number;
            subdivisions?: number;
            arc?: number;
            faceColors?: Color4[];
            faceUV?: Vector4[];
            hasRings?: boolean;
            enclose?: boolean;
            cap?: number;
            sideOrientation?: number;
            frontUVs?: Vector4;
            backUVs?: Vector4;
        }) {
            const height: number = options.height || 2;
            let diameterTop: number = options.diameterTop === 0 ? 0 : options.diameterTop || options.diameter || 1;
            let diameterBottom: number =
                options.diameterBottom === 0 ? 0 : options.diameterBottom || options.diameter || 1;
            diameterTop = diameterTop || 0.00001; // Prevent broken normals
            diameterBottom = diameterBottom || 0.00001; // Prevent broken normals
            const tessellation: number = options.tessellation || 24;
            const subdivisions: number = options.subdivisions || 1;
            const hasRings: boolean = options.hasRings ? true : false;
            const enclose: boolean = options.enclose ? true : false;
            const cap = options.cap === 0 ? 0 : options.cap || Mesh.CAP_ALL;
            const arc: number = options.arc && (options.arc <= 0 || options.arc > 1) ? 1.0 : options.arc || 1.0;
            const sideOrientation: number =
                options.sideOrientation === 0 ? 0 : options.sideOrientation || VertexData.DEFAULTSIDE;
            const faceUV: Vector4[] = options.faceUV || new Array<Vector4>(3);
            const faceColors = options.faceColors;
            // default face colors and UV if undefined
            const quadNb: number = arc !== 1 && enclose ? 2 : 0;
            const ringNb: number = hasRings ? subdivisions : 1;
            const surfaceNb: number = 2 + (1 + quadNb) * ringNb;
            let f: number;

            for (f = 0; f < surfaceNb; f++) {
                if (faceColors && faceColors[f] === undefined) {
                    faceColors[f] = new Color4(1, 1, 1, 1);
                }
            }
            for (f = 0; f < surfaceNb; f++) {
                if (faceUV && faceUV[f] === undefined) {
                    faceUV[f] = new Vector4(0, 0, 1, 1);
                }
            }

            const indices = new Array<number>();
            const positions = new Array<number>();
            const normals = new Array<number>();
            const uvs = new Array<number>();
            const colors = new Array<number>();

            const angle_step = (Math.PI * 2 * arc) / tessellation;
            let angle: number;
            let h: number;
            let radius: number;
            const tan = (diameterBottom - diameterTop) / 2 / height;
            const ringVertex: Vector3 = Vector3.Zero();
            const ringNormal: Vector3 = Vector3.Zero();
            const ringFirstVertex: Vector3 = Vector3.Zero();
            const ringFirstNormal: Vector3 = Vector3.Zero();
            const quadNormal: Vector3 = Vector3.Zero();
            const Y: Vector3 = Axis.Y;

            // positions, normals, uvs
            let i: number;
            let j: number;
            let r: number;
            let ringIdx = 1;
            let s = 1; // surface index
            let cs = 0;
            let v = 0;

            for (i = 0; i <= subdivisions; i++) {
                h = i / subdivisions;
                radius = (h * (diameterTop - diameterBottom) + diameterBottom) / 2;
                ringIdx = hasRings && i !== 0 && i !== subdivisions ? 2 : 1;
                for (r = 0; r < ringIdx; r++) {
                    if (hasRings) {
                        s += r;
                    }
                    if (enclose) {
                        s += 2 * r;
                    }

                    for (j = 0; j <= tessellation; j++) {
                        angle = j * angle_step;

                        // position
                        ringVertex.x = Math.cos(-angle) * radius;
                        ringVertex.y = -height / 2 + h * height;
                        ringVertex.z = Math.sin(-angle) * radius;

                        // normal
                        if (diameterTop === 0 && i === subdivisions) {
                            // if no top cap, reuse former normals
                            ringNormal.x = normals[normals.length - (tessellation + 1) * 3];
                            ringNormal.y = normals[normals.length - (tessellation + 1) * 3 + 1];
                            ringNormal.z = normals[normals.length - (tessellation + 1) * 3 + 2];
                        } else {
                            ringNormal.x = ringVertex.x;
                            ringNormal.z = ringVertex.z;
                            ringNormal.y = Math.sqrt(ringNormal.x * ringNormal.x + ringNormal.z * ringNormal.z) * tan;
                            ringNormal.normalize();
                        }

                        // keep first ring vertex values for enclose
                        if (j === 0) {
                            ringFirstVertex.copyFrom(ringVertex);
                            ringFirstNormal.copyFrom(ringNormal);
                        }

                        positions.push(ringVertex.x, ringVertex.y, ringVertex.z);
                        normals.push(ringNormal.x, ringNormal.y, ringNormal.z);
                        if (hasRings) {
                            v = cs !== s ? faceUV[s].y : faceUV[s].w;
                        } else {
                            v = faceUV[s].y + (faceUV[s].w - faceUV[s].y) * h;
                        }
                        uvs.push(faceUV[s].x + ((faceUV[s].z - faceUV[s].x) * j) / tessellation, v);
                        if (faceColors) {
                            colors.push(faceColors[s].r, faceColors[s].g, faceColors[s].b, faceColors[s].a);
                        }
                    }

                    // if enclose, add four vertices and their dedicated normals
                    if (arc !== 1 && enclose) {
                        positions.push(ringVertex.x, ringVertex.y, ringVertex.z);
                        positions.push(0, ringVertex.y, 0);
                        positions.push(0, ringVertex.y, 0);
                        positions.push(ringFirstVertex.x, ringFirstVertex.y, ringFirstVertex.z);
                        Vector3.CrossToRef(Y, ringNormal, quadNormal);
                        quadNormal.normalize();
                        normals.push(
                            quadNormal.x,
                            quadNormal.y,
                            quadNormal.z,
                            quadNormal.x,
                            quadNormal.y,
                            quadNormal.z
                        );
                        Vector3.CrossToRef(ringFirstNormal, Y, quadNormal);
                        quadNormal.normalize();
                        normals.push(
                            quadNormal.x,
                            quadNormal.y,
                            quadNormal.z,
                            quadNormal.x,
                            quadNormal.y,
                            quadNormal.z
                        );
                        if (hasRings) {
                            v = cs !== s ? faceUV[s + 1].y : faceUV[s + 1].w;
                        } else {
                            v = faceUV[s + 1].y + (faceUV[s + 1].w - faceUV[s + 1].y) * h;
                        }
                        uvs.push(faceUV[s + 1].x, v);
                        uvs.push(faceUV[s + 1].z, v);
                        if (hasRings) {
                            v = cs !== s ? faceUV[s + 2].y : faceUV[s + 2].w;
                        } else {
                            v = faceUV[s + 2].y + (faceUV[s + 2].w - faceUV[s + 2].y) * h;
                        }
                        uvs.push(faceUV[s + 2].x, v);
                        uvs.push(faceUV[s + 2].z, v);
                        if (faceColors) {
                            colors.push(
                                faceColors[s + 1].r,
                                faceColors[s + 1].g,
                                faceColors[s + 1].b,
                                faceColors[s + 1].a
                            );
                            colors.push(
                                faceColors[s + 1].r,
                                faceColors[s + 1].g,
                                faceColors[s + 1].b,
                                faceColors[s + 1].a
                            );
                            colors.push(
                                faceColors[s + 2].r,
                                faceColors[s + 2].g,
                                faceColors[s + 2].b,
                                faceColors[s + 2].a
                            );
                            colors.push(
                                faceColors[s + 2].r,
                                faceColors[s + 2].g,
                                faceColors[s + 2].b,
                                faceColors[s + 2].a
                            );
                        }
                    }
                    if (cs !== s) {
                        cs = s;
                    }
                }
            }

            // indices
            const e: number = arc !== 1 && enclose ? tessellation + 4 : tessellation; // correction of number of iteration if enclose
            let sI: number;
            i = 0;
            for (sI = 0; sI < subdivisions; sI++) {
                let i0 = 0;
                let i1 = 0;
                let i2 = 0;
                let i3 = 0;
                for (j = 0; j < tessellation; j++) {
                    i0 = i * (e + 1) + j; // top left
                    i1 = (i + 1) * (e + 1) + j; // bottom left
                    i2 = i * (e + 1) + (j + 1); // top right
                    i3 = (i + 1) * (e + 1) + (j + 1); // bottom right
                    indices.push(i0, i1, i2);
                    indices.push(i3, i2, i1);
                }
                if (arc !== 1 && enclose) {
                    // if enclose, add two quads
                    indices.push(i0 + 2, i1 + 2, i2 + 2);
                    indices.push(i3 + 2, i2 + 2, i1 + 2);
                    indices.push(i0 + 4, i1 + 4, i2 + 4);
                    indices.push(i3 + 4, i2 + 4, i1 + 4);
                } else if (arc === 1) {
                    indices.push(i2, i3, i * (e + 1));
                    indices.push((i + 1) * (e + 1), i * (e + 1), i3);
                }
                i = hasRings ? i + 2 : i + 1;
            }

            // Caps
            const createCylinderCap = (isTop: boolean) => {
                const radius = isTop ? diameterTop / 2 : diameterBottom / 2;
                if (radius === 0) {
                    return;
                }

                // Cap positions, normals & uvs
                let angle;
                let circleVector;
                let i: number;
                const u: Vector4 = isTop ? faceUV[surfaceNb - 1] : faceUV[0];
                let c: Nullable<Color4> = null;
                if (faceColors) {
                    c = isTop ? faceColors[surfaceNb - 1] : faceColors[0];
                }
                // cap center
                const vbase = positions.length / 3;
                const offset = isTop ? height / 2 : -height / 2;
                const center = new Vector3(0, offset, 0);
                positions.push(center.x, center.y, center.z);
                normals.push(0, isTop ? 1 : -1, 0);
                uvs.push(u.x + (u.z - u.x) * 0.5, u.y + (u.w - u.y) * 0.5);
                if (c) {
                    colors.push(c.r, c.g, c.b, c.a);
                }

                const textureScale = new Vector2(0.5, 0.5);
                for (i = 0; i <= tessellation; i++) {
                    angle = i * angle_step;
                    const cos = Math.cos(-angle);
                    const sin = Math.sin(-angle);
                    circleVector = new Vector3(cos * radius, offset, sin * radius);
                    const textureCoordinate = new Vector2(cos * textureScale.x + 0.5, sin * textureScale.y + 0.5);
                    positions.push(circleVector.x, circleVector.y, circleVector.z);
                    normals.push(0, isTop ? 1 : -1, 0);
                    uvs.push(u.x + (u.z - u.x) * textureCoordinate.x, u.y + (u.w - u.y) * textureCoordinate.y);
                    if (c) {
                        colors.push(c.r, c.g, c.b, c.a);
                    }
                }
                // Cap indices
                for (i = 0; i < tessellation; i++) {
                    if (!isTop) {
                        indices.push(vbase);
                        indices.push(vbase + (i + 1));
                        indices.push(vbase + (i + 2));
                    } else {
                        indices.push(vbase);
                        indices.push(vbase + (i + 2));
                        indices.push(vbase + (i + 1));
                    }
                }
                // Close the loop
                if (arc === 1) {
                    if (!isTop) {
                        indices.push(vbase, vbase + tessellation + 1, vbase + 1);
                    } else {
                        indices.push(vbase, vbase + 1, vbase + tessellation + 1);
                    }
                }
            };

            // add caps to geometry based on cap parameter
            if (cap === Mesh.CAP_START || cap === Mesh.CAP_ALL) {
                createCylinderCap(false);
            }
            if (cap === Mesh.CAP_END || cap === Mesh.CAP_ALL) {
                createCylinderCap(true);
            }

            // Sides
            VertexData._ComputeSides(
                sideOrientation,
                positions,
                indices,
                normals,
                uvs,
                options.frontUVs,
                options.backUVs
            );

            const vertexData = new VertexData();

            vertexData.indices = indices;
            vertexData.positions = positions;
            vertexData.normals = normals;
            vertexData.uvs = uvs;
            if (faceColors) {
                vertexData.colors = colors;
            }

            return vertexData;
        }
    }
}
